import { ChangeEvent, FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi } from '@services/authApi'
import './PortalLoginPage.css'

/**
 * Forgot-password page (KAN-782). Posts the email to the enumeration-safe
 * `POST /api/v2/auth/password/forgot` endpoint and ALWAYS shows the same generic confirmation,
 * so the UI never reveals whether an account exists for the address.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    setError(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await authApi.forgotPassword(email)
      // Generic, existence-agnostic confirmation regardless of the backend outcome.
      setSubmitted(true)
    } catch {
      // Even on a transport error, keep the message generic so we don't leak account state.
      setSubmitted(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="portal-login portal-login-customer">
      <div className="portal-login__layout">
        <div className="portal-login__card">
          <header className="portal-login__header">
            <span className="portal-login__badge">RESET PASSWORD</span>
            <h1>Forgot your password?</h1>
            <div className="portal-login__description">
              <p>Enter your account email and we'll send you a link to reset your password.</p>
            </div>
          </header>

          {submitted ? (
            <div className="portal-login__form">
              <p className="portal-login__otp-hint">
                If an account exists for that email, a password reset link has been sent. The link
                expires shortly and can be used once.
              </p>
              <button
                type="button"
                className="portal-login__submit"
                onClick={() => navigate('/login')}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form className="portal-login__form" onSubmit={handleSubmit}>
              <label className="portal-login__form-label" htmlFor="forgot-email">
                Email
              </label>
              <input
                id="forgot-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={handleChange}
                disabled={isSubmitting}
              />

              {error && <div className="portal-login__error">{error}</div>}

              <button type="submit" className="portal-login__submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <footer className="portal-login__footer">
            <button
              type="button"
              className="portal-login__secondary"
              onClick={() => navigate('/login')}
              disabled={isSubmitting}
            >
              Back to Sign In
            </button>
          </footer>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
